import {orderActions} from 'app/constant/order'
import {ViewOrder} from 'app/features/orders/models/OrderState'
import {
  fetchOrder,
  selectViewOrder,
  selectViewOrderHistory,
} from 'app/features/orders/redux/order/orderSlice'
import {showMessage} from 'app/redux/messageSlice'
import ordersService from 'app/services/OrdersService'
import clsx from 'clsx'
import React, {useMemo, useState} from 'react'
import {Dropdown, DropdownItem} from 'react-bootstrap'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from 'setup'
import find from 'lodash/find'
import toUpper from 'lodash/toUpper'
import {orderStatusType} from 'app/features/orders/models'
import {eventNames, orderTypes, paymentStatusTypes} from 'app/constant'
import OrderActionConfirmationDialog from './OrderActionConfirmationDialog'
import {filter} from 'lodash'
import {mapToMigrationActionModel, mapToOrderActionModel, mapToPosOrderActionModel} from './helper'
import eventPublishService from 'app/services/EventPublishService'
import {useFeatureFlag} from 'app/features/context/FeatureFlagContext'

const pmStatus = [
  paymentStatusTypes.pending,
  paymentStatusTypes.failed,
  paymentStatusTypes.refunded,
]

interface Props {
  hideRefundAndRestock: boolean
}

const OrderActions: React.FC<Props> = ({hideRefundAndRestock}) => {
  const dispatch = useDispatch()
  const order = useSelector<RootState, ViewOrder>(selectViewOrder)
  const [selectedAction, setSelectedAction] = useState<any>()
  const [showDialog, setShowDialog] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const orderHistory = useSelector<RootState, any[]>(selectViewOrderHistory)
  const {isPosOrderEditEnabled} = useFeatureFlag()

  const isRestockedOrder = useMemo(() => {
    return (
      filter(
        orderHistory,
        ({orderEditId, orderStatusSummary}) =>
          !orderEditId &&
          orderStatusSummary &&
          orderStatusSummary?.orderActionType === orderActions.restockOnly
      ).length > 0
    )
  }, [orderHistory])

  const getOrderActions = useMemo(() => {
    const {financialStatus, status, orderType} = order
    if (pmStatus.includes(financialStatus)) {
      return []
    } else if (status === orderStatusType.migration) {
      return mapToMigrationActionModel(order)
    } else if (orderType === orderTypes.POS && !isPosOrderEditEnabled) {
      return mapToPosOrderActionModel(order)
    }
    return mapToOrderActionModel(order, hideRefundAndRestock, isRestockedOrder)
  }, [order, hideRefundAndRestock, isRestockedOrder])

  const handleSelect = (eventKey: any) => {
    const action = find(getOrderActions, {value: eventKey})
    if (action) {
      setShowDialog(true)
      setSelectedAction(action)
    }
  }

  const handleConfirm = async (values: any) => {
    try {
      let response: any
      setIsSubmitting(true)
      if (selectedAction.value === orderActions.refundAndRestock && order.isPaidByGiftCard) {
        response = await ordersService.cancelOrder(order.id, values.reason, values.sendEmail)
        if (response?.status === 'success') {
          await eventPublishService.publish({message: {id: order.id}, name: eventNames.orderCancel})
        }
      } else {
        response = await ordersService.processOrderAction({
          ...values,
          orderId: order.id,
          actionType: toUpper(selectedAction.value),
        })
      }
      if (response?.status === 'success') {
        dispatch(
          showMessage({
            text: `Successfully updated Order #${order.orderNumber} to ${selectedAction.title}`,
            variant: 'success',
          })
        )
        // redirect(`/orders/${order.id}/fulfillments/view`)
        dispatch(fetchOrder(order.id))
      } else {
        if (response?.error) {
          dispatch(showMessage({text: response?.error, variant: 'error'}))
          return
        }
        dispatch(
          showMessage({
            text: `Error updating Order #${order.orderNumber} to ${selectedAction.title}`,
            variant: 'error',
          })
        )
      }
    } catch (error) {
      console.error(error)
      dispatch(
        showMessage({
          text: `Error updating Order #${order.orderNumber} to ${selectedAction.title}`,
          variant: 'error',
        })
      )
    } finally {
      setIsSubmitting(false)
      setShowDialog(false)
    }
  }

  const handleClose = () => {
    setShowDialog(false)
  }

  if (getOrderActions && getOrderActions.length === 0) {
    return null
  }

  return (
    <>
      <Dropdown
        className={clsx('d-flex justify-content-end align-items-center flex-row me-5')}
        onSelect={handleSelect}
      >
        <Dropdown.Toggle
          variant='transparent'
          id='dropdown-basic'
          className='form-select btn btn-md explore-btn-outline'
        >
          Actions{'  '}
        </Dropdown.Toggle>
        <Dropdown.Menu className='align-items-center'>
          {getOrderActions.map((item: any) => {
            return (
              <DropdownItem as='button' eventKey={item.value} className='btn btn-sm fs-'>
                {item.name}
              </DropdownItem>
            )
          })}
        </Dropdown.Menu>
      </Dropdown>
      {showDialog && (
        <OrderActionConfirmationDialog
          {...selectedAction}
          title={selectedAction?.name}
          onConfirm={handleConfirm}
          onClose={handleClose}
          isSubmitting={isSubmitting}
          isRefundAndRestock={orderActions.refundAndRestock === selectedAction?.value}
        />
      )}
    </>
  )
}

export default OrderActions
